import { useState, useEffect, useCallback, useRef } from 'react';

const IDLE_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'scroll'] as const;

/**
 * Detects user inactivity. Returns `isIdle` (true after `timeout` ms of no input)
 * and `wake()` to manually reset the timer.
 *
 * @param timeout - Idle timeout in milliseconds (default 3 minutes)
 * @param enabled - Whether idle detection is active (disable during calls, etc.)
 */
export function useIdleDetector(timeout = 180_000, enabled = true) {
  const [isIdle, setIsIdle] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!enabled) return;

    setIsIdle(false);
    timerRef.current = setTimeout(() => setIsIdle(true), timeout);
  }, [timeout, enabled]);

  const wake = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) {
      setIsIdle(false);
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // Start the timer
    resetTimer();

    // Listen for user activity
    const handler = () => resetTimer();
    for (const event of IDLE_EVENTS) {
      document.addEventListener(event, handler, { passive: true });
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      for (const event of IDLE_EVENTS) {
        document.removeEventListener(event, handler);
      }
    };
  }, [enabled, resetTimer]);

  return { isIdle, wake };
}
